import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import SectionHeader from './SectionHeader';

const TestimonialSection = styled.section`
  background-color: ${props => props.theme.colors.lightColor};
  padding: 80px 0;
`;

const TestimonialContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const SliderWrapper = styled.div`
  position: relative;
  overflow: hidden;
  margin-bottom: 30px;
`;

const SliderContent = styled.div`
  display: flex;
  transition: transform 0.5s ease;
  transform: translateX(-${props => props.currentIndex * 100}%);
`;

const TestimonialCard = styled.div`
  flex: 0 0 100%;
  padding: 30px;
  background-color: ${props => props.theme.colors.white};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadows.regular};
  opacity: 0;
  transform: translateY(20px);
  animation: fadeIn 0.5s forwards;
  
  @keyframes fadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TestimonialContent = styled.div`
  margin-bottom: 20px;
  
  p {
    font-style: italic;
    color: ${props => props.theme.colors.grey[600]};
    font-size: ${props => props.theme.fontSizes.medium};
    line-height: 1.8;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 20px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorInfo = styled.div`
  h4 {
    margin-bottom: 5px;
    color: ${props => props.theme.colors.secondaryColor};
  }
  
  p {
    color: ${props => props.theme.colors.grey[600]};
  }
`;

const SliderControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const SliderButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.theme.colors.primaryColor};
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${props => props.theme.transitions.main};
  
  &:hover {
    background-color: ${props => props.active 
      ? props.theme.colors.primaryColor 
      : props.theme.colors.grey[400]};
  }
`;

// New Slider Indicators components
const SliderIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
`;

const Indicator = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.active 
    ? props.theme.colors.primaryColor 
    : props.theme.colors.grey[300]};
  cursor: pointer;
  transition: ${props => props.theme.transitions.main};
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryColor};
  }
`;

const TestimonialSlider = ({ title, subtitle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Sample testimonial data
  const testimonials = [
    {
      id: 1,
      content: "The TNPSC coaching at KALS Academy was comprehensive and well-structured. The faculty's guidance was instrumental in my success.",
      authorName: "Student Name",
      authorPosition: "TNPSC Qualified, 2024",
      authorImage: "/placeholder/80/80"
    },
    {
      id: 2,
      content: "The practical approach to structural design teaching helped me develop real-world skills that proved valuable in my career.",
      authorName: "Student Name",
      authorPosition: "Civil Engineer, Company Name",
      authorImage: "/placeholder/80/80"
    },
    {
      id: 3,
      content: "The knowledge and expertise shared by the faculty at KALS Academy have been invaluable in my professional growth as a structural engineer.",
      authorName: "Student Name",
      authorPosition: "Structural Engineer, Company Name",
      authorImage: "/placeholder/80/80"
    }
  ];
  
  const totalTestimonials = testimonials.length;
  
  const nextSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex === totalTestimonials - 1 ? 0 : prevIndex + 1));
      
      // Reset animation flag after transition completes
      setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
  };
  
  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex === 0 ? totalTestimonials - 1 : prevIndex - 1));
      
      // Reset animation flag after transition completes
      setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
  };
  
  const goToSlide = (index) => {
    if (!isAnimating && index !== currentIndex) {
      setIsAnimating(true);
      setCurrentIndex(index);
      
      // Reset animation flag after transition completes
      setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
  };
  
  // Auto slide every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [currentIndex]);
  
  return (
    <TestimonialSection>
      <div className="container">
        <SectionHeader title={title} subtitle={subtitle} />
        
        <TestimonialContainer>
          <SliderWrapper>
            <SliderContent currentIndex={currentIndex}>
              {testimonials.map((testimonial) => (
                <TestimonialCard key={testimonial.id}>
                  <TestimonialContent>
                    <p>"{testimonial.content}"</p>
                  </TestimonialContent>
                  <TestimonialAuthor>
                    <AuthorImage>
                      <img src={testimonial.authorImage} alt={testimonial.authorName} />
                    </AuthorImage>
                    <AuthorInfo>
                      <h4>{testimonial.authorName}</h4>
                      <p>{testimonial.authorPosition}</p>
                    </AuthorInfo>
                  </TestimonialAuthor>
                </TestimonialCard>
              ))}
            </SliderContent>
          </SliderWrapper>
          
          <SliderControls>
            <SliderButton onClick={prevSlide} aria-label="Previous testimonial">
              <FaChevronLeft />
            </SliderButton>
            <SliderButton onClick={nextSlide} aria-label="Next testimonial">
              <FaChevronRight />
            </SliderButton>
          </SliderControls>
          
          <SliderIndicators>
            {testimonials.map((_, index) => (
              <Indicator 
                key={index} 
                active={index === currentIndex} 
                onClick={() => goToSlide(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </SliderIndicators>
        </TestimonialContainer>
      </div>
    </TestimonialSection>
  );
};

export default TestimonialSlider;